import { type OperationVariables } from '@apollo/react-common';
import { type GraphQLTaggedNode } from 'react-relay';
import { type FetchQueryFetchPolicy } from 'relay-runtime';
import { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import { isRelayEnabled } from './isRelayEnabled.ts';

import { fetchQuery } from './fetchQuery.ts';
import { toDocumentNode } from './toDocumentNode.ts';
import { apolloCompatibleQuery } from './apolloCompatibleQuery.ts';

export type QueryInteropSupportedResult<TData> = {
	data: TData;
	error?: ApolloErrorInterop;
};

export type QueryInteropFetchPolicy = 'cache-first' | 'network-only' | 'cache-only' | 'no-cache';

export type QueryInteropSupportedArgs<
	_TData,
	TVariables extends Record<string, any> = OperationVariables,
> = {
	query: GraphQLTaggedNode;
	variables?: TVariables;
	errorPolicy?: 'none' | 'ignore' | 'all';
	fetchPolicy?: QueryInteropFetchPolicy;
	context?: Record<string, any>;
	/**
	 * Ensures that the interop in Apollo build variant uses the AGG client
	 */
	interopApolloAggClient?: boolean;
	/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
	__callerPackage__?: string;
};

export function queryInterop<TData, TVariables extends Record<string, any> = OperationVariables>(
	params: QueryInteropSupportedArgs<TData, TVariables>,
): Promise<QueryInteropSupportedResult<TData>> {
	return isRelayEnabled(params.__callerPackage__)
		? queryInteropRelay<TData, TVariables>(params)
		: queryInteropApollo<TData, TVariables>(params);
}

function queryInteropRelay<TData, TVariables extends Record<string, any> = OperationVariables>(
	params: QueryInteropSupportedArgs<TData, TVariables>,
): Promise<QueryInteropSupportedResult<TData>> {
	const relayFetchPolicy: FetchQueryFetchPolicy = mapFetchPolicyForQuery(params.fetchPolicy);

	return apolloCompatibleQuery<TData>({
		fetchData: () =>
			fetchQuery<{ response: TData; variables: TVariables }>(
				window.GLOBAL_RELAY_ENV!,
				params.query,
				params.variables ?? ({} as TVariables),
				{ fetchPolicy: relayFetchPolicy, networkCacheConfig: { metadata: params.context } },
			).toPromise() as Promise<TData>,
		convertToApolloError,
	});
}

async function queryInteropApollo<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>({
	query,
	...rest
}: QueryInteropSupportedArgs<TData, TVariables>): Promise<QueryInteropSupportedResult<TData>> {
	const client = rest.interopApolloAggClient
		? window['GLOBAL_APOLLO_AGG_CLIENT']!
		: window['GLOBAL_APOLLO_CLIENT']!;

	const result = await client.query({
		...rest,
		query: toDocumentNode(query),
	});

	return {
		data: result.data,
		error: result.errors?.length
			? new ApolloErrorInterop({ graphQLErrors: result.errors })
			: undefined,
	};
}

// Relay:  https://relay.dev/docs/guided-tour/reusing-cached-data/fetch-policies/
// Apollo: https://www.apollographql.com/docs/react/data/queries#supported-fetch-policies
function mapFetchPolicyForQuery(
	fetchPolicy: QueryInteropFetchPolicy | undefined,
): FetchQueryFetchPolicy {
	switch (fetchPolicy) {
		case 'no-cache':
		case 'network-only':
			return 'network-only';
		case 'cache-first':
		case 'cache-only': // Ideally, for 'cache-only' we should just attempt to do a store read. In the absence of `readQuery` shim, we're temporarily defaulting to `'store-or-network'`
		default:
			return 'store-or-network';
	}
}

export function convertToApolloError(error: Error): ApolloErrorInterop {
	// TODO: Implement proper error conversion
	return error as ApolloErrorInterop;
}
